import { PencilSimpleIcon, PlusIcon, UsersThreeIcon, XIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { Fragment, type ReactNode, type KeyboardEvent, useMemo, useRef, useState } from "react";
import { channelFits, type ProductKind } from "@zooi/domain";
import { api, q } from "./api.ts";
import { dateTime, day } from "./format.ts";
import { CHANNEL_RULES_LABELS as R, KIT_LABELS, LIBRARY_LABELS, POLICY_LABELS as L } from "./labels.ts";
import { channelRules, rulesByChannel } from "./method-library.ts";
import { POLICY_GROUPS, PolicyMetadata, WorkspacePolicyForm } from "./policies.tsx";
import { Button, DetailPanel, ErrorText, Section } from "./ui.tsx";
import { channelName, ChannelLogo, ChannelName } from "./ui/channel.tsx";
import { GroupHeader, ListRow } from "./ui/list.tsx";
import { PropertyList, PropertyRow } from "./ui/props.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { Chip } from "./ui/status.tsx";

// Playbooks › Channel rules (docs/design-system.md §0.6, §0.3d): one row per
// channel, grouped by who may write the text there, then the workspace's own
// rules; a row opens its rules beside the list. The kinds of rule (platform,
// zooi.ai default, workspace, tactic) are sections of a channel's detail, not
// nested groups of the list.


type Rule = ReturnType<typeof channelRules>[number];
type Tier = "person" | "agent";

/** A channel where any rule keeps model-written text out is one where only the person writes. */
export const channelTier = (rules: Pick<Rule, "enforcement">[]): Tier => (rules.some((rule) => rule.enforcement !== "checklist") ? "person" : "agent");

/** The newest date a platform rule's source was checked on, if any was. */
const lastChecked = (rules: Rule[]) => rules.filter((rule) => rule.kind === "platform_rule" && rule.verification === "verified" && rule.checked_at).map((rule) => rule.checked_at!).sort().at(-1) ?? null;
const checkedText = (at: string) => (/^\d{4}-\d{2}-\d{2}$/.test(at) ? day(at) : dateTime(at));

/** `channel:<id>`, `policy:<id>` or `policy:new`: what the detail beside the list shows. */
export type RuleSelection = string;

/** A platform rule last checked more than 90 days ago, or never, is due to be checked against its source again. */
const RECHECK_DAYS = 90;
export const recheckDue = (rules: Pick<Rule, "kind" | "verification" | "checked_at">[], today: string) => rules.some((rule) => rule.kind === "platform_rule"
  && (rule.verification !== "verified" || !rule.checked_at || Date.parse(`${today}T00:00:00Z`) - Date.parse(`${rule.checked_at.slice(0, 10)}T00:00:00Z`) > RECHECK_DAYS * 86_400_000));

export function ChannelRules({ kind, all, onAll, workspaceId, canEdit, selected, onSelect }: { kind: ProductKind | null; all: boolean; onAll: (all: boolean) => void; workspaceId: string; canEdit: boolean; selected: RuleSelection | undefined; onSelect: (next: RuleSelection | undefined, replace?: boolean) => void }) {
  const knowledge = useQuery(q.knowledge);
  const workspace = useQuery({ ...q.workspacePolicies(workspaceId), enabled: Boolean(workspaceId) });
  const rules = useMemo(() => (knowledge.data ? channelRules(knowledge.data.policies, workspace.data ?? []) : []), [knowledge.data, workspace.data]);
  const every = useMemo(() => rulesByChannel(rules).map((entry) => ({ ...entry, tier: channelTier(entry.rules) })), [rules]);
  // A product sees the channels its kind grows on; the rest are one click away (docs/product-decisions.md 2026-10-31).
  const channels = kind && !all ? every.filter((entry) => channelFits(kind, entry.channel)) : every;
  const hidden = every.length - channels.length;
  const today = new Date().toISOString().slice(0, 10);
  const groups: { tier: Tier; label: string; note: string; channels: typeof channels }[] = [
    { tier: "person", label: R.personTier, note: R.personTierNote, channels: channels.filter((entry) => entry.tier === "person") },
    { tier: "agent", label: R.agentTier, note: R.agentTierNote, channels: channels.filter((entry) => entry.tier === "agent") },
  ];
  const policies = workspace.data ?? [];
  const order = [...groups.flatMap((group) => group.channels.map((entry) => `channel:${entry.channel}`)), ...policies.map((policy) => `policy:${policy.id}`)];
  const [cursor, setCursor] = useState<string | null>(selected ?? null);
  const rows = useRef(new Map<string, HTMLLIElement>());
  const current = order.find((key) => key === (selected ?? cursor)) ?? order[0];
  const close = () => { onSelect(undefined); requestAnimationFrame(() => { if (current) rows.current.get(current)?.focus(); }); };
  const move = (delta: number) => {
    if (!order.length) return;
    const index = Math.max(0, order.indexOf(current ?? ""));
    const next = order[Math.min(order.length - 1, Math.max(0, index + delta))];
    setCursor(next);
    rows.current.get(next)?.focus();
    if (selected && next !== selected) onSelect(next, true);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    if (key === "j" || key === "ArrowDown") move(1);
    else if (key === "k" || key === "ArrowUp") move(-1);
    else if (key === "Enter" && current) onSelect(current);
    else if (key === "Escape" && selected) close();
    else return;
    event.preventDefault();
  };
  const row = (key: string) => ({
    id: `rule-${key.replace(":", "-")}`, selected: key === current,
    onSelect: () => setCursor(key), onOpen: () => onSelect(key),
    rowRef: (node: HTMLLIElement | null) => { if (node) rows.current.set(key, node); else rows.current.delete(key); },
  });
  if (knowledge.isLoading || workspace.isLoading) return <Skeleton shape="groups" />;
  const channel = selected?.startsWith("channel:") ? channels.find((entry) => `channel:${entry.channel}` === selected) : undefined;
  const policy = selected?.startsWith("policy:") ? policies.find((entry) => `policy:${entry.id}` === selected) : undefined;
  return <div className={`z-split${selected ? " has-detail" : ""}`}>
    <div className="z-flow">
      <ErrorText error={knowledge.error ?? workspace.error} />
      {kind && (hidden > 0 || all) && <p className="z-small z-muted"><button type="button" className="z-link" onClick={() => onAll(!all)}>{all ? R.showFit : R.showOthers(every.filter((entry) => !channelFits(kind, entry.channel)).length)}</button></p>}
      <ul className="z-rows z-boxed" role="listbox" aria-label={LIBRARY_LABELS.tabs.rules} onKeyDown={onKeyDown}>
        {groups.map((group) => group.channels.length > 0 && <Fragment key={group.tier}>
          <li role="presentation"><GroupHeader label={group.label} count={group.channels.length} note={group.note} /></li>
          {group.channels.map((entry) => {
            const checked = lastChecked(entry.rules);
            return <ListRow key={entry.channel} {...row(`channel:${entry.channel}`)} icon={<ChannelLogo channel={entry.channel} />} title={channelName(entry.channel)}
              sub={(entry.rules.find((rule) => rule.kind === "platform_rule") ?? entry.rules[0])?.rule}
              chips={<><Chip>{R.ruleCount(entry.rules.length)}</Chip>{recheckDue(entry.rules, today) && <Chip tone="amber">{R.recheck}</Chip>}{kind && !channelFits(kind, entry.channel) && <Chip>{R.otherKinds}</Chip>}</>}
              meta={checked ? R.checkedOn(checkedText(checked)) : undefined} />;
          })}
        </Fragment>)}
        <li role="presentation"><GroupHeader label={R.workspaceTier} count={policies.length} note={R.workspaceTierNote}>
          {canEdit && <Button size="sm" tone="ghost" onClick={() => onSelect("policy:new")}><PlusIcon size={14} /> {R.newPolicy}</Button>}
        </GroupHeader></li>
        {policies.map((entry) => <ListRow key={entry.id} {...row(`policy:${entry.id}`)} icon={<UsersThreeIcon size={16} />} title={entry.rule}
          sub={entry.channels.map(channelName).join(", ")}
          chips={!entry.active && <Chip>{L.paused}</Chip>}
          meta={L.updated(entry.updated_at)} />)}
        {!policies.length && workspace.isSuccess && <li className="z-lrow z-muted z-small" role="presentation">{canEdit ? L.workspaceEmpty : L.workspaceReadOnly}</li>}
      </ul>
      <p className="z-small z-muted">{LIBRARY_LABELS.rulesNote}</p>
    </div>
    {selected && <DetailPanel label={channel ? channelName(channel.channel) : R.workspaceTier} onClose={close}>
      {channel ? <ChannelDetail channel={channel.channel} tier={channel.tier} rules={channel.rules} onClose={close} />
        : selected === "policy:new" ? <PolicyDetail workspaceId={workspaceId} canEdit={canEdit} onClose={close} />
        : policy ? <PolicyDetail workspaceId={workspaceId} canEdit={canEdit} policy={policy} onClose={close} />
        : <Head title="" onClose={close} />}
    </DetailPanel>}
  </div>;
}

function Head({ title, onClose }: { title: ReactNode; onClose: () => void }) {
  return <div className="z-detail-head">
    <h2>{title}</h2>
    <button type="button" className="z-iconbtn" aria-label={KIT_LABELS.close} title={`${KIT_LABELS.close} (Esc)`} onClick={onClose}><XIcon size={16} /></button>
  </div>;
}

/** A channel's rules by kind, each with its source and, on demand, why it exists; how a rule applies is said once, and on a rule only when code enforces it. */
function ChannelDetail({ channel, tier, rules, onClose }: { channel: string; tier: Tier; rules: Rule[]; onClose: () => void }) {
  return <>
    <Head title={<ChannelName channel={channel} />} onClose={onClose} />
    <PropertyList>
      <PropertyRow label={R.whoWrites}>{tier === "person" ? R.personWrites : R.agentWrites}</PropertyRow>
      <PropertyRow label={R.rules}>{R.ruleCount(rules.length)}</PropertyRow>
      <PropertyRow label={R.howApplied}>{L.enforcement.checklist}</PropertyRow>
    </PropertyList>
    {POLICY_GROUPS.map((kind) => {
      const group = rules.filter((rule) => rule.kind === kind);
      return group.length > 0 && <Section key={kind} title={L.groups[kind]} count={group.length}>
        <div className="z-rules">{group.map((rule) => <div key={rule.id} className="z-rule">
          <p>{rule.rule}</p>
          <PolicyMetadata source={rule.source} verification={rule.verification} checked_at={rule.checked_at} enforcement={rule.enforcement === "checklist" ? undefined : rule.enforcement} />
          {(rule.why || rule.talking_points.length > 0) && <details className="z-disclosure z-small">
            <summary>{rule.why ? L.rationale : L.talkingPoints}</summary>
            <div className="z-flow">
              {rule.why && <p>{rule.why}</p>}
              {rule.talking_points.length > 0 && <ul className="z-points">{rule.talking_points.map((point, index) => <li key={index}>{point}</li>)}</ul>}
            </div>
          </details>}
        </div>)}</div>
      </Section>;
    })}
  </>;
}

/** One workspace rule, or a new one: its text and where it applies; owners and admins pause, resume and edit it. */
function PolicyDetail({ workspaceId, canEdit, policy, onClose }: { workspaceId: string; canEdit: boolean; policy?: C.WorkspacePolicy; onClose: () => void }) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const reload = async () => {
    await client.invalidateQueries({ queryKey: q.workspacePolicies(workspaceId).queryKey });
    await client.invalidateQueries({ queryKey: ["action"] });
    await client.invalidateQueries({ queryKey: ["action-policies"] });
  };
  const toggle = useMutation({ mutationFn: () => api.patch<C.WorkspacePolicy>("/api/workspace/policies/" + policy!.id, { active: !policy!.active } satisfies C.UpdateWorkspacePolicyInput), onSuccess: reload });
  if (!policy) return <>
    <Head title={L.create} onClose={onClose} />
    <p className="z-small z-muted">{L.workspaceNote}</p>
    <WorkspacePolicyForm onSaved={async () => { await reload(); onClose(); }} />
  </>;
  return <>
    <Head title={R.workspaceRule} onClose={onClose} />
    <p className="z-fact-value">{policy.rule}</p>
    <PropertyList>
      <PropertyRow label={L.channels}><span className="z-inline">{policy.channels.map((channel) => <ChannelName key={channel} channel={channel} />)}</span></PropertyRow>
      <PropertyRow label={L.application}>{L.applicationOptions[policy.enforcement as keyof typeof L.applicationOptions] ?? policy.enforcement}</PropertyRow>
      <PropertyRow label={R.state}>{policy.active ? L.enabled : L.paused}</PropertyRow>
      <PropertyRow label={R.version}>{`${L.revision(policy.revision)} · ${L.updated(policy.updated_at)}`}</PropertyRow>
    </PropertyList>
    {canEdit ? <>
      {editing && <WorkspacePolicyForm key={policy.id + ":" + policy.revision} initial={policy} onSaved={async () => { await reload(); setEditing(false); }} />}
      <div className="z-detail-actions">
        <Button busy={toggle.isPending} onClick={() => toggle.mutate()}>{policy.active ? L.pause : L.resume}</Button>
        <Button tone="ghost" icon={editing ? <XIcon /> : <PencilSimpleIcon />} aria-expanded={editing} onClick={() => setEditing(!editing)}>{editing ? L.cancelEdit : L.edit}</Button>
      </div>
      <ErrorText error={toggle.error} />
    </> : <p className="z-small z-muted">{L.workspaceReadOnly}</p>}
  </>;
}
